/**
 * @file CostPriceHistorySection.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 * @description The item window's "Cost Price History": the Xero cost history
 *              and — its own series, never merged — the till's (Epos Now) cost
 *              and sale price history (Cam, 20 Oct 2026: "keep both and offer
 *              both"). Shared by Manage Inventory's item window and the
 *              read-only item window, which said the same thing twice.
 *
 *   history = GET /api/inventory/:id/cost-prices
 *             { points, current_cost, source, till: { ready, reason, product, points } }
 */

import React from 'react';
import CostPriceHistoryChart from './CostPriceHistoryChart';
import { formatDate } from '../utils/dateFormatters';

function CostPriceHistorySection({ history }) {
  const points = history?.points || [];
  const till = history?.till || null;
  // Milk powder's cost is the weekly table's, in another unit from what the
  // till sells by — not drawn on one axis with it.
  const tillDrawn = !!(till && till.ready && (till.points || []).length && history?.source !== 'milk_powder');
  const tillPoints = tillDrawn ? till.points : null;
  return (
    <div className="cost-history-section">
      <h4>Cost Price History</h4>
      {points.length >= 2 || tillDrawn ? (
        <CostPriceHistoryChart points={points} currentCost={history?.current_cost ?? null} till={tillPoints} />
      ) : (
        <p className="cost-history-empty">
          {points.length === 1
            ? `Only one cost price has been recorded (${formatDate(points[0].effective_date)}), so there is no history to chart yet.`
            : 'No cost price history has been recorded for this item.'}
        </p>
      )}
      {till && till.product && (
        <p className="cost-history-note">
          Till prices from Epos Now product {till.product.epos_product_id}
          {till.product.name ? ` (${till.product.name})` : ''}{till.product.manual ? ', linked by hand' : ''}
          {till.product.is_archived ? ' — archived in Epos Now' : ''}.
          {history?.source === 'milk_powder' && tillPoints === null && (till.points || []).length
            ? ' Not drawn: the milk powder cost is weekly, in another unit.' : ''}
        </p>
      )}
      {till && !till.ready && till.reason && <p className="cost-history-note">Till prices: {till.reason}.</p>}
    </div>
  );
}

export default CostPriceHistorySection;
